import { Slider } from '@/components/custom/slider'
import { useDataTable } from '@/components/data-table/data-table-provider'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group'
import { useDebouncedCallback } from '@/hooks/use-debounce'
import { isArrayOfNumbers } from '@/lib/is-array'
import { Timer } from 'lucide-react'
import { useState } from 'react'
import type { DataTableSliderFilterField } from './types'

function getFilter(filterValue: unknown): number[] | null {
  if (typeof filterValue === 'number') return [filterValue, filterValue]
  if (Array.isArray(filterValue) && isArrayOfNumbers(filterValue)) {
    const first = filterValue[0] as number
    if (filterValue.length === 1) return [first, first]
    return filterValue as number[]
  }
  return null
}

// TODO: discuss if we even need the `defaultMin` and `defaultMax`
export function DataTableFilterSlider<TData>({
  value: _value,
  min: defaultMin,
  max: defaultMax,
  unit,
  label,
}: DataTableSliderFilterField<TData>) {
  const value = _value as string
  const { table, columnFilters, getFacetedMinMaxValues } = useDataTable()
  const column = table.getColumn(value)
  const filterValue = columnFilters.find((i) => i.id === value)?.value
  const filters = getFilter(filterValue)
  const [input, setInput] = useState<number[] | null>(filters)
  const [min, max] = getFacetedMinMaxValues?.(table, value) ||
    column?.getFacetedMinMaxValues() || [defaultMin, defaultMax]

  // Sync external filter changes (e.g. reset) back to local input
  const [prevFilters, setPrevFilters] = useState(filters)
  if (prevFilters !== filters) {
    setPrevFilters(filters)
    if (!filters) {
      setInput(null)
    } else if (!input || input[0] !== filters[0] || input[1] !== filters[1]) {
      setInput(filters)
    }
  }

  const setFilterDebounced = useDebouncedCallback((val: number[]) => {
    column?.setFilterValue(val)
  }, 500)

  return (
    <div className="grid gap-3">
      <div className="flex items-center gap-2">
        <InputGroup className="border-neutral-subtle-border bg-app h-10 flex-1 rounded-lg font-mono shadow-none">
          <InputGroupAddon>
            <Timer className="text-neutral-low-contrast h-4 w-4" />
          </InputGroupAddon>
          <InputGroupInput
            placeholder="Min"
            type="number"
            name={`min-${value}`}
            id={`min-${value}`}
            aria-label={`Minimum ${label}`}
            value={input?.[0] ?? ''}
            min={min}
            max={max}
            onChange={(e) => {
              const newInput: number[] = [
                Number(e.target.value),
                input?.[1] ?? max,
              ]
              setInput(newInput)
              setFilterDebounced(newInput)
            }}
          />
          {unit ? (
            <InputGroupAddon align="inline-end">
              <span className="text-neutral-low-contrast text-xs">{unit}</span>
            </InputGroupAddon>
          ) : null}
        </InputGroup>
        <InputGroup className="border-neutral-subtle-border bg-app h-10 flex-1 rounded-lg font-mono shadow-none">
          <InputGroupAddon>
            <Timer className="text-neutral-low-contrast h-4 w-4" />
          </InputGroupAddon>
          <InputGroupInput
            placeholder="Max"
            type="number"
            name={`max-${value}`}
            id={`max-${value}`}
            aria-label={`Maximum ${label}`}
            value={input?.[1] ?? ''}
            min={min}
            max={max}
            onChange={(e) => {
              const newInput: number[] = [
                input?.[0] ?? min,
                Number(e.target.value),
              ]
              setInput(newInput)
              setFilterDebounced(newInput)
            }}
          />
          {unit ? (
            <InputGroupAddon align="inline-end">
              <span className="text-neutral-low-contrast text-xs">{unit}</span>
            </InputGroupAddon>
          ) : null}
        </InputGroup>
      </div>
      <Slider
        min={min}
        max={max}
        value={input?.length === 2 ? input : [min, max]}
        onValueChange={(values) => {
          setInput(values)
          setFilterDebounced(values)
        }}
      />
    </div>
  )
}
